import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';

import DiagnosticFormContext from './diagnostic-context';

const LOCAL_STORAGE_KEY = 'unsavedDiagnosticForms';

// Los borradores son historias clínicas enteras y la cuota (~5 MB) se puede llenar.
// Quedarse sin borrador local es aceptable; tumbar la app con un QuotaExceededError, no.
const escribirEnLocalStorage = (datos) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, datos);
    return true;
  } catch (e) {
    console.warn('No se pudo guardar el borrador de historia clínica:', e);
    return false;
  }
};

export const DiagnosticFormProvider = ({ children }) => {
  const [diagnosticForms, setDiagnosticForms] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setDiagnosticForms(parsed);
      } catch (e) {
        console.error('Error parsing localStorage data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const currentStored = localStorage.getItem(LOCAL_STORAGE_KEY);
    const currentData = JSON.stringify(diagnosticForms);

    if (currentStored !== currentData) {
      escribirEnLocalStorage(currentData);
    }
  }, [diagnosticForms]);

  const updateForm = (admissionId, formData) => {
    setDiagnosticForms((prev) => {
      const existingIndex = prev.findIndex((f) => f.AdmissionID === admissionId);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...formData };
        return updated;
      }
      return [...prev, { AdmissionID: admissionId, ...formData }];
    });
  };

  const getForm = (admissionId) => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const forms = JSON.parse(stored);
      return forms.find((f) => f.AdmissionID === admissionId);
    }
    return null;
  };

  const removeForm = (admissionId) => {
    const updatedForms = diagnosticForms.filter((f) => f.AdmissionID !== admissionId);
    setDiagnosticForms(updatedForms);

    // Aquí el fallo SÍ importa: `getForm` lee de localStorage, no del estado, así que un
    // borrador que sobreviva al guardado reaparece como "cambios sin guardar" y vuelve a
    // pisar el registro de la BD. Si no se puede reescribir la lista, se borra la clave.
    if (!escribirEnLocalStorage(JSON.stringify(updatedForms))) {
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } catch (e) {
        console.error('El borrador de historia clínica sigue vivo en localStorage:', e);
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      diagnosticForms,
      updateForm,
      getForm,
      removeForm,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diagnosticForms]
  );

  return (
    <DiagnosticFormContext.Provider value={contextValue}>{children}</DiagnosticFormContext.Provider>
  );
};

DiagnosticFormProvider.propTypes = {
  children: PropTypes.node,
};
